// ClassSwitcher.js
// Pill showing the active class, with a dropdown menu (HomeSelect) to
// switch between the teacher's classes. Renders nothing for zero classes,
// and a static (non-interactive) label when the teacher has exactly one.
// See docs/superpowers/specs/2026-09-16-home-redesign.md §5, §11 (WP9).
import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { homeColors } from "../homeTheme";
import HomeSelect from "../primitives/HomeSelect";

const identity = (s) => s;

// `/classes/mine` (backend/classes.py:248) does not return a per-class
// student count today. If a caller (or a future backend change) attaches one
// of these fields to a class object, it is shown; otherwise the pill shows
// the class name alone rather than inventing a number.
const studentCount = (cls) => {
  const count = cls?.student_count ?? cls?.total_students ?? cls?.num_students;
  return Number.isFinite(count) ? count : null;
};

const classLabel = (cls, t) => {
  const name = cls?.class_name || "";
  const count = studentCount(cls);
  return count == null ? name : t("{name} · {count} students", { name, count });
};

const ClassSwitcher = ({
  classes = [],
  selectedClassId = null,
  onChange,
  isDarkMode = false,
  t = identity,
}) => {
  const colors = homeColors(isDarkMode);

  if (!Array.isArray(classes) || classes.length === 0) {
    return null;
  }

  if (classes.length === 1) {
    return (
      <View testID="class-switcher-static" style={styles.staticLabel}>
        <MaterialCommunityIcons name="account-group" size={16} color={colors.textSecondary} />
        <Text style={[styles.labelText, { color: colors.textPrimary }]}>
          {classLabel(classes[0], t)}
        </Text>
      </View>
    );
  }

  const selected =
    classes.find((cls) => String(cls.class_id) === String(selectedClassId)) || classes[0];

  const items = classes.map((cls) => ({
    key: cls.class_id,
    label: cls.class_name,
    sublabel: cls.school_name || null,
  }));

  return (
    <HomeSelect
      icon="account-group"
      valueLabel={classLabel(selected, t)}
      items={items}
      selectedKey={selected?.class_id}
      onChange={(key) => onChange?.(Number(key))}
      isDarkMode={isDarkMode}
      t={t}
      testID="class-switcher"
    />
  );
};

const styles = StyleSheet.create({
  staticLabel: { flexDirection: "row", alignItems: "center", gap: 8 },
  labelText: { fontSize: 13, fontWeight: "600" },
});

export default ClassSwitcher;
